import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { MaskReveal } from '@/components/motion/Reveal';
import { useFeaturedProjects } from '@/lib/cms';

interface Props {
  headline: string;
  view_all_label: string;
  view_all_href: string;
  count?: number;
}

export function HomeFeaturedProjects({ headline, view_all_label, view_all_href, count = 3 }: Props) {
  const { data: projects = [], isLoading } = useFeaturedProjects(count);

  return (
    <MaskReveal className="section-y-lg bg-bg-subtle/30" tilt={-1.5} duration={1.3} sheen>
      <div className="container-wide">
        <FadeIn>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Selected work</p>
          <div className="mt-3 flex items-end justify-between gap-6">
            <h2 className="text-h2 text-fg">{headline}</h2>
            <Link
              to={view_all_href}
              className="hidden items-center gap-1 text-sm text-gold-500 hover:underline md:inline-flex"
            >
              {view_all_label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeIn>

        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <StaggerItem key={`p-${i}`}>
                  <Card className="h-full animate-pulse overflow-hidden">
                    <div className="aspect-video w-full bg-bg-subtle" />
                    <CardHeader>
                      <div className="h-5 w-3/4 rounded bg-bg-subtle" />
                      <div className="mt-2 h-4 w-full rounded bg-bg-subtle" />
                    </CardHeader>
                  </Card>
                </StaggerItem>
              ))
            : projects.length === 0
              ? (
                <StaggerItem>
                  <Card className="col-span-full p-10 text-center text-sm text-fg-subtle">
                    No featured projects yet. Mark a project as "featured" in the admin portal.
                  </Card>
                </StaggerItem>
              )
              : projects.map((project) => (
                  <StaggerItem key={project.id}>
                    <Link to={`/projects/${project.slug}`} className="group block h-full">
                      <Card className="h-full overflow-hidden transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
                        <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-gold-500/20 to-bg-elevated">
                          {project.hero_image_url ? (
                            <img
                              src={project.hero_image_url}
                              alt={`${project.title} hero image`}
                              loading="lazy"
                              decoding="async"
                              className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-widest text-fg-subtle">
                              {project.title}
                            </div>
                          )}
                        </div>
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Badge variant="gold">{project.category}</Badge>
                            <Badge>{labelForStatus(project.status)}</Badge>
                          </div>
                          <CardTitle className="mt-3">{project.title}</CardTitle>
                          <CardDescription>{project.summary}</CardDescription>
                          <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold-500">
                            Read case study
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </CardHeader>
                      </Card>
                    </Link>
                  </StaggerItem>
                ))}
        </Stagger>
      </div>
    </MaskReveal>
  );
}

function labelForStatus(status: 'shipped' | 'ongoing' | 'concept'): string {
  switch (status) {
    case 'shipped':
      return 'Shipped';
    case 'ongoing':
      return 'Ongoing';
    case 'concept':
      return 'Concept';
  }
}